import type React from "react";
import { useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Button } from "./Button";
import {
  FaFileImage,
  FaFileVideo,
  FaCheck,
  FaTimes,
  FaRedo,
  FaTrash,
} from "react-icons/fa";
import { CheckCircle, XCircle, X } from "lucide-react";
import VideoThumbnail from "../listings/VideoThumbnail";
import { type FileUploadState, UploadStatus } from "../listings/types";
import VideoProcessingMessage from "./VideoProcessingMessage";

interface CompletedMediaGridProps {
  completedFiles: FileUploadState[];
  failedFiles: FileUploadState[];
  cancelledFiles: FileUploadState[];
  onRemove: (fileId: string) => void;
  onRetry: (fileId: string) => void;
}

interface MediaOverlayProps {
  fileState: FileUploadState;
  onClose: () => void;
  onRemove: (fileId: string) => void;
  onRetry: (fileId: string) => void;
}

const MediaOverlay: React.FC<MediaOverlayProps> = ({
  fileState,
  onClose,
  onRemove,
  onRetry,
}) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${
      sizes[i]
    }`;
  };

  const isLimitError = Boolean(
    fileState.error &&
      (fileState.error.includes("exceeds the maximum allowed length") ||
        fileState.error.includes("Exceeds maximum size") ||
        fileState.error.includes("Exceeds maximum video size"))
  );
  const canRetry =
    fileState.status === UploadStatus.FAILED &&
    fileState.retryCount < 3 &&
    !isLimitError;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 max-h-[70vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-primary">Media Details</h3>
          <button
            onClick={onClose}
            className="mr-2 text-gray-500 hover:text-red-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Preview */}
          <div className="mb-4">
            {fileState.file.type.startsWith("image/") ? (
              <img
                src={fileState.preview || "/placeholder.svg"}
                alt={fileState.file.name}
                className="w-full max-h-48 object-contain rounded-lg border"
              />
            ) : (
              <VideoThumbnail
                videoUrl={fileState.preview}
                className="w-full max-h-48 min-h-[150px] object-contain rounded-lg border"
              />
            )}
          </div>

          {/* File Info */}
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">
                File Name
              </label>
              <p className="text-sm text-gray-900 break-all">
                {fileState.file.name}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Size
                </label>
                <p className="text-sm text-gray-900">
                  {formatFileSize(fileState.file.size)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Type
                </label>
                <p className="text-sm text-gray-900">{fileState.file.type}</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Status
              </label>
              <div className="flex items-center mt-1">
                {fileState.status === UploadStatus.COMPLETED && (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    <span className="text-sm text-green-600">Completed</span>
                  </>
                )}
                {fileState.status === UploadStatus.FAILED && (
                  <>
                    <XCircle className="h-4 w-4 text-red-500 mr-2" />
                    <span className="text-sm text-red-600">Failed</span>
                  </>
                )}
                {fileState.status === UploadStatus.CANCELLED && (
                  <>
                    <XCircle className="h-4 w-4 text-gray-500 mr-2" />
                    <span className="text-sm text-gray-600">Cancelled</span>
                  </>
                )}
              </div>
            </div>

            {fileState.error && (
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Error
                </label>
                <p className="text-sm text-red-600">{fileState.error}</p>
              </div>
            )}

            {fileState.needsConversion && (
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Video Processing Required
                </label>
                <VideoProcessingMessage />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
            {canRetry && (
              <Button
                size="sm"
                onClick={() => {
                  onRetry(fileState.id);
                  onClose();
                }}
              >
                <FaRedo className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                onRemove(fileState.id);
                onClose();
              }}
            >
              <FaTrash className="h-4 w-4 mr-2" />
              Remove
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CompletedMediaGrid: React.FC<CompletedMediaGridProps> = ({
  completedFiles,
  failedFiles,
  cancelledFiles,
  onRemove,
  onRetry,
}) => {
  const isMobile = useIsMobile(700);
  const [selectedFile, setSelectedFile] = useState<FileUploadState | null>(
    null
  );

  const allCompletedFiles = [
    ...completedFiles,
    ...failedFiles,
    ...cancelledFiles,
  ];

  if (allCompletedFiles.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mt-4">
        <h4 className="text-sm font-medium text-primary mb-3">
          Completed Media ({allCompletedFiles.length})
        </h4>

        {/* Grid of thumbnails */}
        <div className={`grid gap-2 ${isMobile ? "grid-cols-3" : "grid-cols-6"}`}>
          {allCompletedFiles.map((fileState) => (
            <div
              key={fileState.id}
              className="relative aspect-square cursor-pointer group"
              onClick={() => setSelectedFile(fileState)}
            >
              {/* Thumbnail */}
              <div className="w-full h-full rounded-md overflow-hidden border-2 border-transparent group-hover:border-secondary transition-colors">
                {fileState.file.type.startsWith("image/") ? (
                  <img
                    src={fileState.preview || "/placeholder.svg"}
                    alt={fileState.file.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <VideoThumbnail
                    videoUrl={fileState.preview}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Status indicator */}
              <div className="absolute top-1 right-1">
                {fileState.status === UploadStatus.COMPLETED && (
                  <div className="bg-green-500 rounded-full p-1">
                    <FaCheck className="text-white" size={8} />
                  </div>
                )}
                {fileState.status === UploadStatus.FAILED && (
                  <div className="bg-red-500 rounded-full p-1">
                    <FaTimes className="text-white" size={8} />
                  </div>
                )}
                {fileState.status === UploadStatus.CANCELLED && (
                  <div className="bg-gray-500 rounded-full p-1">
                    <FaTimes className="text-white" size={8} />
                  </div>
                )}
              </div>

              {/* File type indicator */}
              <div className="absolute bottom-1 left-1">
                {fileState.file.type.startsWith("image/") ? (
                  <FaFileImage
                    className="text-secondary bg-opacity-50 rounded p-0.5"
                    size={20}
                  />
                ) : (
                  <FaFileVideo
                    className="text-secondary bg-opacity-50 rounded p-0.5"
                    size={20}
                  />
                )}
              </div>

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-md" />
            </div>
          ))}
        </div>
      </div>

      {/* Overlay */}
      {selectedFile && (
        <MediaOverlay
          fileState={selectedFile}
          onClose={() => setSelectedFile(null)}
          onRemove={onRemove}
          onRetry={onRetry}
        />
      )}
    </>
  );
};

export default CompletedMediaGrid;
